import { useState, useEffect, useReducer } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { sortBy, cloneDeep, find, filter } from 'lodash'
import { MapContainer, TileLayer, Marker, Tooltip } from 'react-leaflet'
import {
  useListSessionsWS,
  useSessionWS,
  useRestricted,
  useLang,
  useUser,
} from '../utils/hooks'

import BaliseList from '../components/BaliseList'
import PageSelect from '../components/PageSelect'
import TabMeteo from '../components/TabMeteo'
import ChangeView from '../components/ChangeView'
import MeteoChart from '../components/MeteoChart'
import Help from '../components/Help'
import HelpNotif from '../components/HelpNotif'
import { langMeteo } from '../assets/langMeteo'
import { green_spot, blue_spot } from '../components/MarkerIcons/MarkerIcons'
import { StyledMapContainer } from '../components/StyledElements'
import {
  do_meteo_chart_options,
  getDistanceBetween,
} from '../assets/assets_functions'
import {
  meteoNumberOfItemsPerPage,
  k_one_hour,
  k_R,
  k_radius,
  k_max_duration_wm,
  k_max_duration_pioupiou,
  k_kmh_to_knts,
} from '../assets/colors'
import { useRef } from 'react'
import Footer from '../components/Footer'

const SORTS = {
  NONE: (list) => list,
  NAME_SORT: (list) => sortBy(list, 'baliseName'),
  ID_SORT: (list) => sortBy(list, 'baliseId'),
  LAT_SORT: (list) => sortBy(list, 'baliseLat'),
  LON_SORT: (list) => sortBy(list, 'baliseLng'),
  ALT_SORT: (list) => sortBy(list, 'baliseAlt'),
  DIST_SORT: (list) => sortBy(list, 'baliseDist'),
}

const Meteo = ({ data }) => {
  const { listSessionsWS, setListSessionsWS } = useListSessionsWS()
  const { sessionWS, changeSessionWS } = useSessionWS()
  const { restricted } = useRestricted()

  const { lang } = useLang()
  const { user } = useUser()
  const [saving, setSaving] = useState(false)

  let navigateTo = useNavigate()
  const { state } = useLocation()

  const exampleSessionId = process.env.REACT_APP_EXAMPLE_SESSION_ID

  if (state) {
    data = state
  }

  const firstDiv = useRef(null)

  const choosenLang = lang === 'eng' ? langMeteo.eng : langMeteo.fr

  const demoUserName = process.env.REACT_APP_DEMO_USERNAME

  const balisesWMReducer = (state, action) => {
    switch (action.type) {
      case 'BALISES_FETCH_INIT':
        return { ...state, isLoading: true, isError: false }
      case 'BALISES_FETCH_OK':
        return {
          ...state,
          initialData: cloneDeep(action.payload),
          data: cloneDeep(action.payload),
          listPage: cloneDeep(
            action.payload.slice(0, meteoNumberOfItemsPerPage),
          ),
          isLoading: false,
          isError: false,
          totalItems: action.payload.length,
          sortOrder: { sortType: 'DIST_SORT', isReverse: false },
        }
      case 'NEW_PAGE':
        return {
          ...state,
          listPage: cloneDeep(
            state.data.slice(
              (action.payload - 1) * meteoNumberOfItemsPerPage,
              action.payload * meteoNumberOfItemsPerPage,
            ),
          ),
          selectedPage: action.payload,
        }
      case 'BALISES_FETCH_FAILED':
        return { ...state, isLoading: false, isError: true }
      case 'SORT_CHANGED':
        return {
          ...state,
          data: cloneDeep(action.payload.list),
          listPage: cloneDeep(
            action.payload.list.slice(
              (state.selectedPage - 1) * meteoNumberOfItemsPerPage,
              state.selectedPage * meteoNumberOfItemsPerPage,
            ),
          ),
          sortOrder: action.payload.sortOrder,
        }
      case 'NEW_ITEM_SELECTED':
        return {
          ...state,
          data: cloneDeep(action.payload),
          listPage: cloneDeep(
            action.payload.slice(
              (state.selectedPage - 1) * meteoNumberOfItemsPerPage,
              state.selectedPage * meteoNumberOfItemsPerPage,
            ),
          ),
          totalItems: action.payload.length,
        }
      case 'SEARCH':
        return {
          ...state,
          data: [...action.payload.data],
          listPage: cloneDeep(
            action.payload.data.slice(0, meteoNumberOfItemsPerPage),
          ),
          selectedPage: 1,
          totalItems: action.payload.data.length,
          sortOrder: { sortType: 'NONE', isReverse: false },
          searchInput: action.payload.searchValue,
        }
      default:
        throw new Error()
    }
  }

  const [listBalisesWM, dispatchListBalisesWM] = useReducer(balisesWMReducer, {
    data: [],
    initialData: [],
    listPage: [],
    isLoading: false,
    isError: false,
    selectedPage: 1,
    totalItems: 0,
    sortOrder: { sortType: 'NONE', isReverse: true },
    searchInput: '',
  })

  const [selectedItemWM, setSelectedItemWM] = useState(null)
  const [mapDataWM, setMapDataWM] = useState({ center: null, bounds: null })
  const [chartOptions, setChartOptions] = useState(null)
  const [stationChartOptions, setStationChartOptions] = useState(null)

  const [showHelp, setShowHelp] = useState(false)

  const saveOneWindstat = (data) => {
    const url = '/api/windstat/saveBaliseHisto'
    const dc = decodeURIComponent(document.cookie)
    const sdc = dc.split('=')[1]
    const ws = sdc.split(';')[0]
    fetch(url, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        authorization: 'ws ' + ws,
      },
      body: JSON.stringify(data),
    })
    return new Promise((resolve, reject) => {
      fetch(url)
        .then((response) => {
          console.log('ws saved')
          resolve(data)
        })
        .catch((error) => {
          console.log('Error saving windstat ' + data, error)
          reject(error)
        })
    })
  }

  const saveBaliseHisto = () => {
    setSaving(true)
    let promises = []
    selectedItemWM.datas.forEach((data) =>
      promises.push(
        saveOneWindstat({ ...data, baliseId: selectedItemWM.baliseId }),
      ),
    )
    Promise.all(promises)
      .then((datas) => {
        console.log('windstats saved')
        const url = '/api/sessionWS/updateSessionMeteo'
        const data = {
          sessionId: sessionWS._id,
          baliseId: selectedItemWM.baliseId,
        }
        const dc = decodeURIComponent(document.cookie)
        const sdc = dc.split('=')[1]
        const ws = sdc.split(';')[0]
        fetch(url, {
          method: 'post',
          headers: {
            'Content-Type': 'application/json',
            authorization: 'ws ' + ws,
          },
          body: JSON.stringify(data),
        })
          .then((response) => response.json())
          .then((jsonResponse) => {
            const temp = Array.from(listSessionsWS.data)
            const temp_index = temp.findIndex(
              (session) => session._id === sessionWS._id,
            )
            temp[temp_index].meteoStationId = selectedItemWM.baliseId

            setListSessionsWS({ type: 'CHANGED_METEO_DATA', payload: temp })
            changeSessionWS({
              ...sessionWS,
              meteoStationId: selectedItemWM.baliseId,
            })
            setSaving(false)
          })
          .catch((error) => {
            console.log(
              'Error updating xxx meteo session ' + sessionWS._id,
              error,
            )
            setSaving(false)
          })
      })
      .catch((error) => {
        if (error.response) {
          console.log('Error getting data winds mobi ' + sessionWS._id)
          console.log(error.response.data)
          console.log(error.response.status)
          console.log(error.response.headers)
          setSaving(false)
        }
      })
  }

  useEffect(() => {
    if (!sessionWS) {
      const localUserId = localStorage.getItem('wsurf_userId')
      if (!localUserId || Number(localUserId) === -1) navigateTo('/login')
      else navigateTo('/sessions/')
    }
  }, [])

  useEffect(() => {
    if (firstDiv.current !== null) {
      const topScroll = firstDiv.current.getBoundingClientRect().top
      window.scrollTo({ top: topScroll.current - 120, behavior: 'smooth' })
    }
  }, [])

  useEffect(() => {
    const fetchForWindStations = async (startLat, startLng, k_radius) => {
      const url =
        'https://winds.mobi/api/2.3/stations/?near-lat=' +
        startLat +
        '&near-lon=' +
        startLng +
        '&near-distance=' +
        k_radius
      return await new Promise((resolve, reject) => {
        fetch(url, {
          method: 'get',
          headers: { 'Content-Type': 'application/json' },
        })
          .then((response) => {
            response.json().then((data) => {
              resolve(data)
            })
          })
          .catch((e) => {
            console.error(e)
          })
      })
    }

    const getBaliseHisto = async (baliseId, duration, start, end) => {
      let url
      switch (baliseId.substring(0, 8)) {
        case 'pioupiou':
          const pioupiouBaliseId = baliseId.substr(9)
          url =
            'https://api.pioupiou.fr/v1/archive/' +
            pioupiouBaliseId +
            '?start=' +
            new Date(start).toISOString() +
            '&stop=' +
            new Date(end).toISOString()
          break
        default:
          url =
            'https://winds.mobi/api/2.3/stations/' +
            baliseId +
            '/historic/?duration=' +
            duration +
            '&keys=_id&keys=w-dir&keys=w-avg&keys=w-max'
          break
      }
      return await new Promise((resolve, reject) => {
        fetch(url)
          .then((response) => response.json())
          .then((jsonResponse) => {
            //console.log(jsonResponse)
            resolve({ baliseId: baliseId, data: jsonResponse })
          })
          .catch((error) => {
            console.log(
              'Error getting winds mobi histo for balise ' + baliseId,
              error,
            )
            reject(error)
          })
      })
    }

    if (!sessionWS) return
    const startHisto =
      new Date(sessionWS.restrictedStartSession).getTime() - k_one_hour
    const endHisto =
      new Date(sessionWS.restrictedStartSession).getTime() +
      sessionWS.restrictedSessionDuration * 1000 +
      k_one_hour
    const now = new Date().getTime()
    const tempDuration = Math.round((now - startHisto) / 1000)
    console.log('startHisto ' + startHisto + ' endHisto ' + endHisto)

    // const url = '/api/windsmobi/getStations'
    // const my_data = JSON.stringify({
    //   // trkpts: base64Str,
    //   startLat: sessionWS.startLat,
    //   startLng: sessionWS.startLng,
    //   k_radius: k_radius,
    // })
    // fetch(url, {
    //   method: 'post',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: my_data,
    // })
    // fetch(url)
    fetchForWindStations(sessionWS.startLat, sessionWS.startLng, k_radius)
      .then((data) => {
        let temp_liste_balises_wm = []
        data.forEach((balise) => {
          if (balise._id !== sessionWS.meteoStationId) {
            const temp_balise_coord = {
              lat: balise.loc.coordinates[1],
              lng: balise.loc.coordinates[0],
            }
            const temp_dist = Math.round(
              getDistanceBetween(
                { lat: sessionWS.startLat, lng: sessionWS.startLng },
                temp_balise_coord,
                k_R,
              ) / 1000,
            )
            temp_liste_balises_wm.push({
              baliseId: balise._id,
              baliseName: balise.name,
              baliseStatus: balise.status,
              baliseLastMeasurement: balise.last['w-avg'],
              baliseLat: balise.loc.coordinates[1],
              baliseLng: balise.loc.coordinates[0],
              baliseAlt: balise.alt,
              baliseDist: temp_dist,
              bg: '',
            })
          }
        })

        let promises = []
        temp_liste_balises_wm.forEach((balise) => {
          switch (balise.baliseId.substr(0, 8)) {
            case 'pioupiou':
              if (tempDuration < k_max_duration_pioupiou) {
                promises.push(
                  getBaliseHisto(
                    balise.baliseId,
                    tempDuration,
                    startHisto,
                    endHisto,
                  ),
                )
              }
              break
            default:
              if (tempDuration < k_max_duration_wm) {
                promises.push(
                  getBaliseHisto(
                    balise.baliseId,
                    tempDuration,
                    startHisto,
                    endHisto,
                  ),
                )
              }
              break
          }
        })
        Promise.all(promises)
          .then((datas) => {
            datas.forEach((data) => {
              const tempBaliseId = data.baliseId
              const balise = find(
                temp_liste_balises_wm,
                (balise) => balise.baliseId === tempBaliseId,
              )
              switch (tempBaliseId.substr(0, 8)) {
                case 'pioupiou':
                  if (balise.baliseStatus === 'green') {
                    balise.datas = sortBy(
                      data.data.data.map((one) => {
                        return {
                          timeU: new Date(one[0]).getTime(),
                          minSpeed: one[3],
                          maxSpeed: one[5],
                          avgSpeed: one[4],
                          direction: one[6],
                        }
                      }),
                      (data) => {
                        return data[0]
                      },
                    )
                  } else balise.datas = undefined
                  break
                default:
                  let tempDatas = []
                  data.data.forEach((one) => {
                    if (
                      one._id * 1000 <= endHisto &&
                      one._id * 1000 >= startHisto
                    ) {
                      tempDatas.push({
                        timeU: one._id * 1000,
                        minSpeed: 0,
                        maxSpeed: one['w-max'],
                        avgSpeed: one['w-avg'],
                        direction: one['w-dir'],
                      })
                    }
                  })

                  balise.datas = sortBy(tempDatas, 'timeU')
                  break
              }
            })
            const sortFunction = SORTS['DIST_SORT']
            temp_liste_balises_wm = temp_liste_balises_wm.map((balise) => {
              return balise.datas === undefined
                ? { ...balise, datas: { detail: 'none' } }
                : balise.datas.length === 0
                  ? { ...balise, datas: { detail: 'none' } }
                  : balise
            })
            temp_liste_balises_wm = sortFunction(temp_liste_balises_wm)
            //temp_liste_balises_wm[0].bg = 'selected'
            dispatchListBalisesWM({
              type: 'BALISES_FETCH_OK',
              payload: temp_liste_balises_wm,
            })
            setSelectedItemWM(null)
          })
          .catch((error) => {
            if (error.response) {
              console.log('Error getting data winds mobi ' + sessionWS._id)
              console.log(error.response.data)
              console.log(error.response.status)
              console.log(error.response.headers)
            }
          })
      })

      .catch((error) => {
        console.log('unable to dispatch wind.mobi response', error)
      })

    // .catch((error) => {
    //   console.log('error getting all stations', error)
    //   dispatchListBalisesWM({ type: 'BALISES_FETCH_FAILED' })
    // })}
  }, [sessionWS])

  useEffect(() => {
    if (!sessionWS) return
    const timeU = new Date(sessionWS.restrictedStartSession).getTime()
    const largeSessionStart = timeU - k_one_hour
    const largeSessionEnd =
      timeU + sessionWS.restrictedSessionDuration * 1000 + k_one_hour
    console.log(
      'largeSessionStart ' +
        largeSessionStart +
        ' largeSessionEnd ' +
        largeSessionEnd,
    )
    const data = {
      start: largeSessionStart,
      end: largeSessionEnd,
      baliseId: sessionWS.meteoStationId,
    }
    const url = '/api/windstat/getBetweenDates'
    const dc = decodeURIComponent(document.cookie)
    const sdc = dc.split('=')[1]
    const ws = sdc.split(';')[0]
    fetch(url, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        authorization: 'ws ' + ws,
      },
      body: JSON.stringify(data),
    })
      .then((response) => {
        response.json().then((stats) => {
          const chartData = stats.map((stat) => {
            return {
              timeU: stat.timeU,
              direction: stat.direction,
              avgSpeed: Math.round(stat.avgSpeed * k_kmh_to_knts * 10) / 10,
              minSpeed: Math.round(stat.minSpeed * k_kmh_to_knts * 10) / 10,
              maxSpeed: Math.round(stat.maxSpeed * k_kmh_to_knts * 10) / 10,
            }
          })
          let tempChartOptions = null
          if (chartData.length > 0) {
            tempChartOptions = do_meteo_chart_options(
              chartData,
              new Date(sessionWS.restrictedStartSession).getTime(),
              new Date(sessionWS.restrictedStartSession).getTime() +
                sessionWS.restrictedSessionDuration * 1000,
              sessionWS.meteoStationId,
              choosenLang,
              sessionWS.timeZone,
            )
          }
          setChartOptions(tempChartOptions)
        })
      })
      .catch((error) => {
        console.log('error getting stats for session ' + sessionWS._id, error)
      })
  }, [sessionWS])

  useEffect(() => {
    if (selectedItemWM && sessionWS) {
      const my_lat_max = Math.max(selectedItemWM.baliseLat, sessionWS.startLat)
      const my_lat_min = Math.min(selectedItemWM.baliseLat, sessionWS.startLat)
      const my_lon_max = Math.max(selectedItemWM.baliseLng, sessionWS.startLng)
      const my_lon_min = Math.min(selectedItemWM.baliseLng, sessionWS.startLng)
      const temp_bounds = [
        [my_lat_max, my_lon_max],
        [my_lat_min, my_lon_min],
      ]
      setMapDataWM({
        center: [
          (selectedItemWM.baliseLat + sessionWS.startLat) / 2,
          (selectedItemWM.baliseLng + sessionWS.startLng) / 2,
        ],
        bounds: temp_bounds,
      })
      if (selectedItemWM.datas.detail === undefined) {
        const timeU = new Date(sessionWS.restrictedStartSession).getTime()
        const largeSessionStart = timeU - k_one_hour
        const largeSessionEnd =
          timeU + sessionWS.restrictedSessionDuration * 1000 + k_one_hour
        console.log(
          'largeSessionStartsave ' +
            largeSessionStart +
            ' largeSessionEndsave ' +
            largeSessionEnd,
        )

        const baliseDatas = filter(selectedItemWM.datas, (one) => {
          return one.timeU >= largeSessionStart && one.timeU <= largeSessionEnd
        })
        const kntBaliseDatas = baliseDatas.map((stat) => {
          return {
            timeU: stat.timeU,
            direction: stat.direction,
            avgSpeed: Math.round(stat.avgSpeed * k_kmh_to_knts * 10) / 10,
            minSpeed: Math.round(stat.minSpeed * k_kmh_to_knts * 10) / 10,
            maxSpeed: Math.round(stat.maxSpeed * k_kmh_to_knts * 10) / 10,
          }
        })
        const tempStationChartData = do_meteo_chart_options(
          kntBaliseDatas,
          timeU,
          timeU + sessionWS.restrictedSessionDuration * 1000,
          selectedItemWM.baliseId,
          choosenLang,
        )
        setStationChartOptions(tempStationChartData)
      } else {
        setStationChartOptions(null)
      }
    } else {
      setStationChartOptions(null)
    }
  }, [selectedItemWM, sessionWS])

  const handle_associate = () => {
    saveBaliseHisto()
  }

  const handle_dissociate = () => {
    const dc = decodeURIComponent(document.cookie)
    const sdc = dc.split('=')[1]
    const ws = sdc.split(';')[0]

    const url_session = '/api/sessionWS/updateSessionMeteo'
    const data_session = {
      sessionId: sessionWS._id,
      baliseId: '',
    }

    fetch(url_session, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        authorization: 'ws ' + ws,
      },
      body: JSON.stringify(data_session),
    })
      .then(() => {
        const temp = Array.from(listSessionsWS.data)
        const temp_index = temp.findIndex(
          (session) => session._id === sessionWS._id,
        )
        temp[temp_index].meteoStationId = ''

        setListSessionsWS({ type: 'CHANGED_METEO_DATA', payload: temp })
        changeSessionWS({ ...sessionWS, meteoStationId: '' })
        const stats = chartOptions.xAxis.data
        stats.forEach((stat) => {
          const data_stat = {
            meteoStationId: sessionWS.meteoStationId,
            timeU: stat,
            sessionId: sessionWS._id,
          }
          const url_stat = '/api/sessionWS/getSessionsForStat'
          const dc = decodeURIComponent(document.cookie)
          const sdc = dc.split('=')[1]
          const ws = sdc.split(';')[0]
          fetch(url_stat, {
            method: 'post',
            headers: {
              'Content-Type': 'application/json',
              authorization: 'ws ' + ws,
            },
            body: JSON.stringify(data_stat),
          }).then((response) => {
            response.json().then((nb) => {
              if (nb === 0) {
                const del_url = '/api/windstat/deleteOneWindStat'
                const del_data = { timeU: stat, sessionId: sessionWS._id }
                fetch(del_url, {
                  method: 'post',
                  headers: {
                    'Content-Type': 'application/json',
                    authorization: 'ws ' + ws,
                  },
                  body: JSON.stringify(del_data),
                })
                  .then((res) => {
                    console.log('stat supprimée ' + stat)
                  })
                  .catch((error) => {
                    console.log('Error deleting stat ' + stat, error)
                  })
              }
            })
          })
        })
      })
      .catch((error) => {
        console.log('Error updating meteo session ' + sessionWS._id, error)
      })

    /*  */
  }

  const handleSelectNewItemWM = (item) => {
    const temp = cloneDeep(listBalisesWM.data)
    const old_index = temp
      .map(function (e) {
        return e.bg
      })
      .indexOf('selected')
    let new_index = -1
    if (item !== null) {
      new_index = temp
        .map(function (e) {
          return e.baliseId
        })
        .indexOf(item.baliseId)
    }

    if (old_index !== -1) {
      temp[old_index].bg = ''
    }

    if (old_index !== new_index && new_index !== -1) {
      temp[new_index].bg = 'selected'
      setSelectedItemWM(item)
    } else {
      setSelectedItemWM(null)
    }

    dispatchListBalisesWM({ type: 'NEW_ITEM_SELECTED', payload: temp })
  }

  const handleSortBalisesWM = (column_type) => {
    const sort_order =
      column_type === listBalisesWM.sortOrder.sortType
        ? !listBalisesWM.sortOrder.isReverse
        : true
    let sort_type = column_type
    const sortFunction = SORTS[sort_type]
    const temp = sort_order
      ? sortFunction(Array.from(listBalisesWM.data)).reverse()
      : sortFunction(Array.from(listBalisesWM.data))
    dispatchListBalisesWM({
      type: 'SORT_CHANGED',
      payload: {
        list: temp,
        sortOrder: { sortType: column_type, isReverse: sort_order },
      },
    })
  }

  const handleSearchInputChange = (event) => {
    setSelectedItemWM(null)

    const temp_searchValue = event ? event.target.value : ''
    let temp_data = [...listBalisesWM.initialData]
    if (temp_searchValue !== '') {
      temp_data = temp_data.filter((o) =>
        Object.keys(o).some((k) =>
          o[k]
            .toString()
            .toLowerCase()
            .includes(temp_searchValue.toLowerCase()),
        ),
      )
    }
    dispatchListBalisesWM({
      type: 'SEARCH',
      payload: { data: temp_data, searchValue: temp_searchValue },
    })
  }

  const handleEscSearch = () => {
    setSelectedItemWM(null)
    dispatchListBalisesWM({
      type: 'SEARCH',
      payload: { data: listBalisesWM.initialData, searchValue: '' },
    })
  }

  const handleClickSelectPage = (num_page) => {
    dispatchListBalisesWM({ type: 'NEW_PAGE', payload: parseInt(num_page) })
  }

  const handle_delete_balise_histo = () => {
    const url = '/api/windstat/deleteBaliseHisto'
    const data = { baliseId: selectedItemWM.baliseId }
    const dc = decodeURIComponent(document.cookie)
    const sdc = dc.split('=')[1]
    const ws = sdc.split(';')[0]
    fetch(url, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        authorization: 'ws ' + ws,
      },
      body: JSON.stringify(data),
    })
      .then((response) => {
        response.json().then((str) => {
          console.log(str)
        })
      })
      .catch((error) => {
        console.log('error getting stats for session ' + sessionWS._id, error)
      })
  }

  const handleHelp = (what) => {
    setShowHelp(what)
  }

  const handleToSessionStats = () => {
    navigateTo('/oneSessionStats/')
  }

  return (
    <>
      {sessionWS && (
        <div>
          <section
            className={'section ' + (user.userId !== -1 ? 'pt-0' : 'pt-4')}
            ref={firstDiv}
          >
            {!showHelp && sessionWS.sessionId !== '' && (
              <Help handleHelp={handleHelp} />
            )}
            {showHelp && (
              <HelpNotif handleHelp={handleHelp} helpText={choosenLang.help} />
            )}
            {user.userId === -1 && (
              <div className="mr-6 mb-2" style={{ textAlign: 'end' }}>
                <button
                  className="button is-primary"
                  onClick={handleToSessionStats}
                >
                  {choosenLang.backToStats}
                </button>
              </div>
            )}

            <div className="columns mt-2">
              <div className="column">
                <div>
                  <TabMeteo
                    timeDomain={
                      restricted ? data.restrictedTimeDomain : data.timeDomain
                    }
                    timeZone={sessionWS.timeZone}
                    lat={sessionWS.startLat}
                    lng={sessionWS.startLng}
                    hasMeteoData={chartOptions !== null}
                    stationHasMeteoData={stationChartOptions !== null}
                    oneStationSelected={selectedItemWM !== null}
                  />
                </div>
                {sessionWS._id !== undefined && (
                  <div>
                    {chartOptions === null && (
                      <div className="tag is-info is-light is-medium has-text-weight-bold">
                        {choosenLang.sessionHasNoMeteoData}
                      </div>
                    )}
                  </div>
                )}
                {(chartOptions !== null || stationChartOptions !== null) && (
                  <div>
                    <div className="block">
                      {chartOptions && (
                        <MeteoChart
                          options={chartOptions}
                          title={choosenLang.sessionHasMeteoData}
                        />
                      )}
                      {sessionWS.meteoStationId !== '' && (
                        <div className="level-right">
                          <button
                            className="button is-primary has-text-white has-text-weight-semibold"
                            onClick={handle_dissociate}
                            disabled={user.userName === demoUserName}
                            title={
                              user.userName === demoUserName
                                ? choosenLang.registeredUsers
                                : ''
                            }
                          >
                            {choosenLang.ungroup}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="column">
                <div className="notification has-background-info-light ">
                  <h1 className="title is-6 has-text-info has-text-weight-semibold">
                    {(chartOptions ? choosenLang.altTitle : choosenLang.title) +
                      k_radius / 1000 +
                      choosenLang.title2}
                  </h1>

                  {listBalisesWM.data.length > 0 && (
                    <BaliseList
                      list={listBalisesWM}
                      handleSelectNewItem={handleSelectNewItemWM}
                      handleSort={handleSortBalisesWM}
                      handleSearchInputChange={handleSearchInputChange}
                      handleEscSearch={handleEscSearch}
                      choosenLang={choosenLang}
                      disabledDemo={user.userName === demoUserName}
                    />
                  )}
                  <PageSelect
                    handleClickSelectPage={handleClickSelectPage}
                    list={listBalisesWM}
                    itemsPerPage={meteoNumberOfItemsPerPage}
                    choosenLang={choosenLang}
                    buttonsActive={true}
                  />
                </div>
                {!stationChartOptions && selectedItemWM ? (
                  <div className="tag is-warning is-light is-medium">
                    {choosenLang.stationHasNoMeteoData}
                  </div>
                ) : selectedItemWM ? (
                  <div className="tag is-info is-light is-medium">
                    {choosenLang.stationHasMeteoData}
                  </div>
                ) : (
                  ''
                )}

                {stationChartOptions && (
                  <div className="notification is-white">
                    <div className="is-justify-content-center">
                      <MeteoChart options={stationChartOptions} title={''} />
                      {sessionWS.meteoStationId === '' && (
                        <button
                          className={
                            'button is-primary has-text-white' +
                            (saving === true ? 'is-loading' : '')
                          }
                          onClick={handle_associate}
                          disabled={
                            sessionWS._id === undefined ||
                            user.userName === demoUserName
                          }
                          title={
                            sessionWS._id === undefined ||
                            user.userName === demoUserName
                              ? choosenLang.registeredUsers
                              : ''
                          }
                        >
                          {choosenLang.group}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {selectedItemWM && mapDataWM.center && (
                  <div>
                    <StyledMapContainer>
                      <MapContainer
                        scrollWheelZoom={true}
                        maxZoom={18}
                        zoomOffset={-1}
                      >
                        <ChangeView
                          center={mapDataWM.center}
                          origin={'meteo'}
                          map_bounds={mapDataWM.bounds}
                          map_path={null}
                        />
                        <TileLayer
                          attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          zIndex="0"
                        />
                        <Marker position={mapDataWM.center} icon={green_spot}>
                          <Tooltip permanent={true}>
                            {'Balise alt ' +
                              selectedItemWM.baliseAlt +
                              ' m - dist ' +
                              selectedItemWM.baliseDist +
                              ' km'}
                          </Tooltip>
                        </Marker>
                        <Marker
                          position={[sessionWS.startLat, sessionWS.startLng]}
                          icon={blue_spot}
                        >
                          <Tooltip permanent={true}>{'Spot '}</Tooltip>
                        </Marker>
                      </MapContainer>
                    </StyledMapContainer>
                  </div>
                )}
              </div>
            </div>
          </section>
          {user.userId === -1 && <Footer />}
        </div>
      )}
    </>
  )
}

export default Meteo
